import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Shuffle } from 'lucide-react';
import nurseryService from '../../../services/nursery';
import { errorMessage } from '../../../utils/apiResponse';
import { cn } from '../../../utils/cn';
import { type DespatchSettings, type PickMode } from '../../../types/nursery';

/**
 * The organisation's default for new loads: let the pickers choose the lots
 * (the default when nothing is saved), or allocate orders before they are
 * picked. A load made after a change
 * takes the new default; loads already made keep theirs, and each load's
 * page can change its own until picking starts.
 *
 * With the pickers choosing by default, confirming an order no longer
 * allocates it by itself. The allocate buttons still do.
 */
export function PickModeSetting() {
  const { t } = useTranslation('nursery', { keyPrefix: 'PickModeSetting' });
  const [settings, setSettings] = useState<DespatchSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    nurseryService.getDespatchSettings()
      .then((res) => {
        if (!live) return;
        if (res.success && res.data) setSettings(res.data);
        else setError(errorMessage(res, t('readFailed')));
      })
      .catch((e) => { if (live) setError(errorMessage(e, t('readFailed'))); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- read once on mount; t only words the fallback error
  }, []);

  const choose = async (mode: PickMode) => {
    if (!settings || settings.default_pick_mode === mode) return;
    setBusy(true);
    setError(null);
    try {
      const res = await nurseryService.saveDespatchSettings({ default_pick_mode: mode });
      if (res.success && res.data) setSettings(res.data);
      else setError(errorMessage(res, t('saveFailed')));
    } catch (e) {
      setError(errorMessage(e, t('saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const current = settings?.default_pick_mode;
  return (
    <section aria-labelledby="pick-mode-setting" className="bg-white border border-neutral-200 rounded-xl p-4 space-y-3">
      <div className="flex items-start gap-2">
        <Shuffle className="h-4 w-4 mt-0.5 text-neutral-500 flex-shrink-0" />
        <div className="min-w-0">
          <h2 id="pick-mode-setting" className="text-sm font-semibold text-neutral-800">{t('title')}</h2>
          <p className="text-sm text-neutral-600 break-words">
            {t('description')}
          </p>
        </div>
      </div>
      {error && <p className="text-sm text-red-700 break-words">{error}</p>}
      <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={t('title')}>
        {(['free', 'allocated'] as PickMode[]).map((mode) => (
          <button
            key={mode}
            type="button"
            role="radio"
            aria-checked={current === mode}
            disabled={busy || !settings}
            onClick={() => choose(mode)}
            className={cn('rounded-lg border px-3 py-2 text-sm font-medium max-w-full',
              current === mode ? 'border-primary-500 bg-primary-50 text-primary-800 ring-1 ring-primary-500' : 'border-neutral-300 text-neutral-700 hover:border-primary-300')}
          >
            {t(`mode.${mode}`)}
          </button>
        ))}
      </div>
    </section>
  );
}

export default PickModeSetting;
